import React, { useRef, useState, useEffect, useMemo } from 'react'
import { Domain, Path } from './types'
import {
    DomainPathFilterSelection,
    EMPTY_SELECTION,
    isFilterActive,
    saveFilterPref
} from './domain-path-filter-preference'

interface Props {
    domains: Domain[]
    paths: Path[]
    value: DomainPathFilterSelection
    onApply: (selection: DomainPathFilterSelection) => void
}

/** Extract unique top-level path prefixes for a domain, e.g. ["/", "/blog/", "/shop/"] */
function getTopLevelPrefixes(paths: Path[], domainId: number): string[] {
    const seen = new Set<string>()
    for (const p of paths) {
        if (p.domain_id !== domainId) continue
        const segments = p.path.split('/').filter(Boolean)
        const prefix = segments.length === 0 ? '/' : '/' + segments[0] + '/'
        seen.add(prefix)
    }
    return Array.from(seen).sort()
}

function getButtonLabel(
    value: DomainPathFilterSelection,
    domains: Domain[]
): string {
    if (!isFilterActive(value)) return 'Alle domæner'

    const selectedDomainIds = Object.keys(value.domains).map(Number)
    const parts = selectedDomainIds.map(id => {
        const domain = domains.find(d => d.id === id)
        const prefixes = value.domains[id]
        if (!prefixes || prefixes.length === 0) return domain?.domain || String(id)
        return `${domain?.domain || id}: ${prefixes.join(', ')}`
    })

    const label = parts.join(' + ')
    return label.length > 42 ? label.slice(0, 42) + '…' : label
}

const DomainPathFilter: React.FC<Props> = ({ domains, paths, value, onApply }) => {
    const [open, setOpen] = useState(false)
    const [alignRight, setAlignRight] = useState(true)
    const [draft, setDraft] = useState<DomainPathFilterSelection>(value)
    const dropdownRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (!open) {
            setDraft(value)
        }
    }, [value, open])

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
                setOpen(false)
            }
        }
        if (open) document.addEventListener('mousedown', handler)
        return () => document.removeEventListener('mousedown', handler)
    }, [open])

    const handleToggle = () => {
        if (!open && dropdownRef.current) {
            const rect = dropdownRef.current.getBoundingClientRect()
            const dropdownWidth = Math.min(340, window.innerWidth * 0.9)
            // If opening left-aligned would overflow the right edge, align right instead
            setAlignRight(rect.left + dropdownWidth > window.innerWidth)
        }
        setOpen(o => !o)
    }

    const prefixesByDomain = useMemo(() => {
        const map: { [domainId: number]: string[] } = {}
        for (const domain of domains) {
            map[domain.id] = getTopLevelPrefixes(paths, domain.id)
        }
        return map
    }, [domains, paths])

    const selectedDomainIds = Object.keys(draft.domains).map(Number)

    const isDomainChecked = (domainId: number) => selectedDomainIds.includes(domainId)

    const isDomainIndeterminate = (domainId: number) => {
        if (!isDomainChecked(domainId)) return false
        const sel = draft.domains[domainId]
        const total = prefixesByDomain[domainId]?.length ?? 0
        return sel && sel.length > 0 && sel.length < total
    }

    const isPrefixChecked = (domainId: number, prefix: string) => {
        if (!isDomainChecked(domainId)) return false
        const sel = draft.domains[domainId]
        if (!sel || sel.length === 0) return true
        return sel.includes(prefix)
    }

    const toggleDomain = (domainId: number) => {
        const next = { ...draft.domains }
        if (next[domainId] !== undefined) {
            delete next[domainId]
        } else {
            next[domainId] = []
        }
        setDraft({ domains: next })
    }

    const togglePrefix = (domainId: number, prefix: string) => {
        const next = { ...draft.domains }
        const allPrefixes = prefixesByDomain[domainId] ?? []

        if (next[domainId] === undefined) {
            next[domainId] = [prefix]
        } else {
            const current = next[domainId]
            const explicit = current.length === 0
                ? allPrefixes.filter(p => p !== prefix)
                : current.includes(prefix)
                    ? current.filter(p => p !== prefix)
                    : [...current, prefix]

            if (explicit.length === 0) {
                delete next[domainId]
            } else if (explicit.length === allPrefixes.length) {
                next[domainId] = []
            } else {
                next[domainId] = explicit
            }
        }
        setDraft({ domains: next })
    }

    const handleClear = () => setDraft(EMPTY_SELECTION)

    const handleApply = () => {
        onApply(draft)
        setOpen(false)
    }

    const handleApplyAndSave = () => {
        onApply(draft)
        saveFilterPref(draft)
        setOpen(false)
    }

    const active = isFilterActive(value)

    return (
        <div ref={dropdownRef} style={{ position: 'relative', display: 'inline-block' }}>
            <button
                className={`btn btn-sm ${active ? 'btn-primary' : 'btn-outline-secondary'}`}
                onClick={handleToggle}
                style={{ whiteSpace: 'nowrap' }}
            >
                Filters{active ? ' ●' : ''}
            </button>

            {open && (
                <div
                    style={{
                        position: 'absolute',
                        top: '100%',
                        ...(alignRight ? { right: 0 } : { left: 0 }),
                        zIndex: 1050,
                        background: '#fff',
                        border: '1px solid #dee2e6',
                        borderRadius: 6,
                        boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                        minWidth: 260,
                        maxWidth: 'min(340px, 90vw)',
                        maxHeight: 400,
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column'
                    }}
                >
                    <div style={{ overflowY: 'auto', minHeight: 0, padding: '8px 0' }}>
                        {domains.length === 0 && (
                            <div style={{ padding: '8px 16px', color: '#888', fontSize: 13 }}>
                                No domains found
                            </div>
                        )}

                        {domains.map(domain => {
                            const topPrefixes = prefixesByDomain[domain.id] ?? []
                            const checked = isDomainChecked(domain.id)
                            const indeterminate = isDomainIndeterminate(domain.id)

                            return (
                                <div key={domain.id}>
                                    <label
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 8,
                                            padding: '5px 14px',
                                            cursor: 'pointer',
                                            fontWeight: 600,
                                            fontSize: 13,
                                            background: checked ? '#f0f4ff' : undefined,
                                            userSelect: 'none',
                                            margin: 0
                                        }}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={checked}
                                            ref={el => { if (el) el.indeterminate = !!indeterminate }}
                                            onChange={() => toggleDomain(domain.id)}
                                            style={{ cursor: 'pointer' }}
                                        />
                                        {domain.domain}
                                    </label>

                                    {checked && topPrefixes.length > 1 && (() => {
                                        const sel = draft.domains[domain.id]
                                        const allSelected = !sel || sel.length === 0
                                        return (
                                            <div style={{ padding: '2px 14px 2px 30px' }}>
                                                <button
                                                    className="btn btn-link p-0"
                                                    style={{ fontSize: 11, color: '#888', textDecoration: 'none' }}
                                                    onClick={() => {
                                                        const next = { ...draft.domains }
                                                        next[domain.id] = allSelected ? [topPrefixes[0]] : []
                                                        setDraft({ domains: next })
                                                    }}
                                                >
                                                    {allSelected ? 'Deselect all' : 'Select all'}
                                                </button>
                                            </div>
                                        )
                                    })()}

                                    {checked && topPrefixes.map(prefix => (
                                        <label
                                            key={prefix}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 8,
                                                padding: '3px 14px 3px 30px',
                                                cursor: 'pointer',
                                                fontSize: 12,
                                                color: '#444',
                                                userSelect: 'none',
                                                margin: 0
                                            }}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={isPrefixChecked(domain.id, prefix)}
                                                onChange={() => togglePrefix(domain.id, prefix)}
                                                style={{ cursor: 'pointer' }}
                                            />
                                            {prefix}
                                        </label>
                                    ))}
                                </div>
                            )
                        })}
                    </div>

                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '8px 12px 4px',
                            borderTop: '1px solid #eee',
                            marginTop: 4,
                            background: '#fff',
                            position: 'sticky',
                            bottom: 0,
                            zIndex: 1
                        }}
                    >
                        <button
                            className="btn btn-sm btn-link text-danger p-0"
                            onClick={handleClear}
                            style={{ fontSize: 12 }}
                        >
                            Clear
                        </button>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <button
                                className="btn btn-sm btn-outline-secondary p-0 px-2"
                                onClick={handleApplyAndSave}
                                style={{ fontSize: 12 }}
                            >
                                Save default
                            </button>
                            <button
                                className="btn btn-sm btn-primary p-0 px-2"
                                onClick={handleApply}
                                style={{ fontSize: 12 }}
                            >
                                Apply
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default DomainPathFilter
